import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PROJECT_DETAILS } from "../data/projects";
import { FadeUp, SectionTag } from "../components/Motion";

const splitStory = (story) => {
  if (story.length < 700) return [story];
  const mid = story.indexOf(". ", Math.floor(story.length / 2));
  return mid > 200 ? [story.slice(0, mid + 1), story.slice(mid + 2)] : [story];
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const idx = PROJECT_DETAILS.findIndex((p) => p.id === projectId);
  const project = PROJECT_DETAILS[idx];

  if (!project) {
    return (
      <div className="mx-auto max-w-[1600px] px-5 py-40 md:px-10" data-testid="project-not-found">
        <h1 className="font-display text-5xl uppercase text-nexa-bone">That page has left the den.</h1>
        <Link to="/work" className="link-line mt-6 inline-block font-mono text-xs uppercase tracking-[0.2em] text-nexa-red" data-testid="not-found-back">
          Back to the ledger
        </Link>
      </div>
    );
  }

  const prev = PROJECT_DETAILS[idx - 1];
  const next = PROJECT_DETAILS[idx + 1];
  const paras = splitStory(project.story);

  return (
    <>
      <section className="relative overflow-hidden border-b border-white/10" data-testid="project-hero">
        <div className="noise-overlay" />
        <div className="mx-auto grid max-w-[1600px] gap-10 px-5 pb-16 pt-28 md:grid-cols-12 md:px-10 md:pb-20 md:pt-36">
          <div className="md:col-span-5">
            <Link to="/work" className="link-line inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-nexa-bone/60" data-testid="project-back-link">
              <ArrowLeft size={14} /> The ledger
            </Link>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-nexa-red">
              {project.sector}{project.year ? ` · ${project.year}` : ""}
            </p>
            <h1 className="mt-4 font-display text-5xl uppercase leading-[0.95] tracking-tight text-nexa-bone sm:text-6xl lg:text-7xl">
              {project.name}
            </h1>
            <ul className="mt-6 flex flex-wrap gap-2">
              {project.terms.map((t) => (
                <li key={t} className="border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-nexa-bone/60">
                  {t}
                </li>
              ))}
            </ul>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-nexa-bone/40">
              Executed at Myndroot — carried forward into Nexa Studio
            </p>
          </div>
          <div className="md:col-span-7">
            <FadeUp>
              <div className="border border-white/15 shadow-[10px_10px_0_0_rgba(238,33,38,0.35)]">
                <img src={project.img} alt={project.name} className="aspect-[16/10] w-full object-cover" data-testid="project-cover" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {project.story && (
        <section className="mx-auto grid max-w-[1600px] gap-8 px-5 py-20 md:grid-cols-12 md:px-10" data-testid="project-story">
          <div className="md:col-span-3">
            <SectionTag>About the project</SectionTag>
          </div>
          <div className="space-y-6 md:col-span-7">
            {paras.map((p, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <p className="text-base leading-relaxed text-nexa-bone/70 md:text-lg">{p}</p>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      {(project.videos.length > 0 || project.gallery.length > 0) && (
        <section className="border-t border-white/10" data-testid="project-gallery">
          <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10">
            <SectionTag>The work</SectionTag>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {project.videos.map((v, i) => (
                <FadeUp key={v} className="md:col-span-2">
                  <div className="border border-white/10" data-testid={`gallery-video-${i}`}>
                    <div className="aspect-video w-full">
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${v}`}
                        title={`${project.name} — film ${i + 1}`}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="h-full w-full"
                      />
                    </div>
                  </div>
                </FadeUp>
              ))}
              {project.gallery.map((g, i) => (
                <FadeUp key={g} delay={(i % 2) * 0.08} className={i % 3 === 0 ? "md:col-span-2" : ""}>
                  <div className="group overflow-hidden border border-white/10">
                    <img
                      src={g}
                      alt={`${project.name} — visual ${i + 1}`}
                      loading="lazy"
                      className={`w-full object-cover grayscale-[30%] transition-all duration-500 group-hover:grayscale-0 ${i % 3 === 0 ? "aspect-[16/7]" : "h-96"}`}
                      data-testid={`gallery-img-${i}`}
                    />
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-white/10" data-testid="project-nav">
        <div className="mx-auto grid max-w-[1600px] md:grid-cols-2">
          <div className="border-b border-white/10 md:border-b-0 md:border-r">
            {prev ? (
              <Link to={`/work/${prev.id}`} className="group block px-5 py-12 md:px-10" data-testid="prev-project-link">
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-nexa-bone/40">
                  <ArrowLeft size={14} /> Previous fight
                </span>
                <span className="mt-3 block font-display text-3xl uppercase leading-tight text-nexa-bone transition-colors duration-300 group-hover:text-nexa-red md:text-4xl">
                  {prev.name}
                </span>
              </Link>
            ) : (
              <div className="px-5 py-12 md:px-10" />
            )}
          </div>
          <div>
            {next ? (
              <Link to={`/work/${next.id}`} className="group block px-5 py-12 text-right md:px-10" data-testid="next-project-link">
                <span className="flex items-center justify-end gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-nexa-bone/40">
                  Next fight <ArrowRight size={14} />
                </span>
                <span className="mt-3 block font-display text-3xl uppercase leading-tight text-nexa-bone transition-colors duration-300 group-hover:text-nexa-red md:text-4xl">
                  {next.name}
                </span>
              </Link>
            ) : (
              <div className="px-5 py-12 md:px-10" />
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProjectDetail;
